import { ArrowLeft, Download, Info, Loader2, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type GenerationResult, useComposerPreview } from "@/lib/api";

import { AddToLibrary } from "./add-to-library";
import { CopyDraftPanel } from "./copy-draft";

interface SlidePreviewPaneProps {
  result: GenerationResult;
  onBack: () => void;
}

/**
 * The built deck, page by page, beside the conversation that made it.
 *
 * Deliberately alongside the chat rather than in place of it: the point is to
 * look at a slide and say what is wrong with it in the same breath, which a
 * full-screen viewer with the ask bar hidden would prevent.
 *
 * What is on screen is LibreOffice's rendering, not PowerPoint's — trustworthy
 * for copy, layout and image crops, less so for template graphics. The note
 * under the header says so rather than letting someone assume otherwise.
 *
 * Written copy is the one file here that is not a thing to look at. Its
 * deliverable is the words, so the pane shows them — and skips the page render
 * entirely, which for a copy .docx could only ever come back as "no preview
 * available, download it to view the deck".
 */
export function SlidePreviewPane({ result, onBack }: SlidePreviewPaneProps) {
  // Only ever set when the file is written copy; every other work mode leaves
  // `copy_draft` null, so a deck or a localized PDF takes the same path it
  // always did. An empty draft falls through too — the panel would render
  // nothing, and a pane with neither words nor a preview is worse than a note.
  const pieces = result.copy_draft?.pieces.length ?? 0;
  const copyDraft = pieces > 0 ? result.copy_draft : null;
  // Disabled rather than fetched and ignored: passing no id is how this hook
  // is told there is nothing to render.
  const {
    data: preview,
    isLoading,
    isError,
  } = useComposerPreview(copyDraft ? undefined : result.artifact_id);

  return (
    <div className="flex h-full flex-col">
      <header className="border-border shrink-0 space-y-2 border-b px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="text-muted hover:text-foreground flex items-center gap-1.5 text-xs transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All elements
        </button>

        <h2 className="text-foreground text-sm font-semibold break-words">{result.filename}</h2>

        {/* Both actions on one row under the name. They are the two things to
            do with a finished file, so they read as a pair rather than as one
            button beside the title and another stranded below it. */}
        <div className="flex flex-wrap items-center gap-2">
          {result.download_url && (
            <Button asChild size="sm" className="shrink-0">
              <a href={result.download_url} download>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download
              </a>
            </Button>
          )}
          <AddToLibrary result={result} size="sm" />
        </div>

        {!copyDraft && preview?.available && (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-muted flex cursor-help items-center gap-1.5 text-[11px]">
                <Info className="h-3 w-3 shrink-0" />
                Approximate rendering
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs leading-5">{preview.note}</TooltipContent>
          </Tooltip>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {copyDraft ? (
          <CopyDraftPanel draft={copyDraft} context="file" />
        ) : isLoading ? (
          <div className="text-muted flex items-center gap-2 py-6 text-xs">
            <Loader2 className="h-4 w-4 animate-spin" />
            Rendering the deck…
          </div>
        ) : isError || !preview ? (
          <PreviewNotice text="Could not render a preview. The .pptx itself is unaffected — download it to view the deck." />
        ) : !preview.available ? (
          <PreviewNotice
            text={
              preview.note || "No preview is available for this file. Download it to view the deck."
            }
          />
        ) : (
          <ol className="space-y-3">
            {(preview.page_urls ?? []).map((url, index) => (
              <li key={url} className="space-y-1">
                <p className="text-muted text-[11px] tabular-nums">Slide {index + 1}</p>
                <img
                  src={url}
                  alt={`Slide ${index + 1}`}
                  className="border-border w-full rounded border shadow-sm"
                  loading="lazy"
                />
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function PreviewNotice({ text }: { text: string }) {
  return (
    <div className="border-outcome-escalated/30 bg-outcome-escalated-tint/60 flex items-start gap-2 rounded-lg border px-3 py-2.5">
      <TriangleAlert className="text-outcome-escalated mt-0.5 h-3.5 w-3.5 shrink-0" />
      <p className="text-foreground/85 text-xs leading-5">{text}</p>
    </div>
  );
}
